import { Component, Input } from '@angular/core';
import { NotificationsService } from '@app/components/notification/NotificationsService';
import { PledgeDao } from '@app/dao/pledge/pledge.dao';
import { AuditPage } from '@app/forms/subscription/definition/interface/audit-page';
import { CommandEnum } from '@app/forms/subscription/definition/interface/enum';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BsModalService } from 'ngx-bootstrap/modal';
import { finalize } from 'rxjs';
import { AuditPageChoiceModalComponent } from './audit-page-choice-modal/audit-page-choice-modal.component';
import { CLIENT_AUDIT_ROUTES } from './audit-pages';

/**
 * Explique que les montants affichés viennent de l'audit, ouvre la page de l'audit dans un nouvel onglet (espace
 * client pour le client, albr-pro pour le conseiller) et resynchronise la souscription depuis l'audit.
 */
@UntilDestroy()
@Component({
  selector: 'ea-audit-sync-actions',
  templateUrl: './audit-sync-actions.component.html',
  styleUrls: ['./audit-sync-actions.component.scss'],
  standalone: false,
})
export class AuditSyncActionsComponent {
  @Input() pages: AuditPage[] = [];

  resyncing = false;

  constructor(
    private subscriptionFacade: SubscriptionFacadeService,
    private pledgeDao: PledgeDao,
    private modalService: BsModalService,
    private notificationService: NotificationsService,
  ) {}

  openAudit() {
    if (this.pages.length === 1) {
      this.openPage(this.pages[0]);
      return;
    }
    this.modalService
      .show(AuditPageChoiceModalComponent, { class: 'ea-modal-plan', initialState: { pages: this.pages } })
      .content.choice.pipe(untilDestroyed(this))
      .subscribe(page => this.openPage(page));
  }

  resync() {
    this.resyncing = true;
    this.subscriptionFacade.loading();
    this.subscriptionFacade
      .putPledge({ ...this.subscriptionFacade.getPledge(), commands: [CommandEnum.RESYNC_FINANCIAL_DATA_FROM_AUDIT] })
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.resyncing = false;
          this.subscriptionFacade.loaded();
        }),
      )
      .subscribe({
        next: () => this.notificationService.success('Montants mis à jour depuis l’audit'),
        error: () => this.notificationService.error('La resynchronisation a échoué', 'Réessayez dans quelques instants.'),
      });
  }

  private openPage(page: AuditPage) {
    if (!this.subscriptionFacade.getUser()?.sessionConsultant) {
      window.open(CLIENT_AUDIT_ROUTES[page], '_blank');
      return;
    }

    const tab = window.open('', '_blank');
    if (!tab) {
      this.notificationService.error(
        'L’audit n’a pas pu être ouvert',
        'Autorisez l’ouverture de fenêtres pour ce site, puis réessayez.',
      );
      return;
    }
    tab.opener = null;
    this.pledgeDao
      .getProAuditLink(this.subscriptionFacade.getPledge().id, page)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: link => (tab.location.href = link),
        error: () => {
          tab.close();
          this.notificationService.error('L’audit n’a pas pu être ouvert', 'Réessayez dans quelques instants.');
        },
      });
  }
}
